import { getGradientForRoute } from "@/utils/routeGradient";
import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Outlet,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { createQueryClient } from "./lib/queryClient";
import { logger } from "./services/logger";
import { generateUUID } from "./utils/uuid";

// Import contexts
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { CollaborationProvider } from "./contexts/CollaborationContext";
import { NavigationProvider } from "./contexts/NavigationContext";
import {
  WebSocketProvider,
  useWebSocketContext,
} from "./contexts/WebSocketContext";
import { useDerivedAssetEvents } from "./hooks/useDerivedAssetEvents";
import { useTimelineExportEvents } from "./hooks/useTimelineExportEvents";
import { useActivityEvents } from "./hooks/useActivityEvents";
import { useUnifiedEventConsumer } from "./hooks/useUnifiedEventConsumer";
import { useExecutionActivityListener } from "./hooks/useExecutionActivityListener";
import { useLoraEvents } from "./hooks/loras";
import { useChatRealtime } from "./hooks/useChatRealtime";

// Import components - kept eager because they're needed for the first paint
// (auth flow, fallbacks) or shape the app shell.
import { AuthPage } from "./pages/AuthPage";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { OAuthHandler } from "./components/auth/OAuthHandler";
import { Header } from "./components/shared/Header";
import { AccountRestrictionBanners } from "./components/billing/RestrictionBanner";
import { NewVersionBanner } from "./components/shared/NewVersionBanner";
import { useAppVersionWatcher } from "./hooks/useAppVersionWatcher";
import { RestrictedAccountGate } from "./components/billing/RestrictedAccountGate";
import { ContactSupportDialogGlobal } from "./components/support/ContactSupportDialog";
import { RouteTransition } from "./components/shared/RouteTransition";
import { MobileOverlay } from "./components/shared/MobileOverlay";
import { useProjects } from "./hooks/useProjects";
import { OrgMembershipGuard } from "@/components/auth/OrgMembershipGuard";
import { AcceptTermsGuard } from "@/components/auth/AcceptTermsGuard";
import { OrgErrorBoundary } from "@/components/org/OrgErrorBoundary";
import { OrganisationLayout } from "@/components/org/OrganisationLayout";
import { ChunkErrorBoundary } from "@/components/shared/ChunkErrorBoundary";
import {
  handleChunkLoadError,
  isChunkLoadError,
} from "@/utils/chunkErrorRecovery";

// Route-level lazy imports - split into their own chunks so the initial
// bundle doesn't drag in the timeline/webcodecs/graph editor stack on
// every page load. Each becomes a separate file in dist/assets/.
const ProjectDashboard = React.lazy(
  () => import("./components/dashboard/ProjectDashboard"),
);
const WebSocketDemo = React.lazy(() =>
  import("./components/debug/WebSocketDemo").then((m) => ({
    default: m.WebSocketDemo,
  })),
);
const NotificationDebug = React.lazy(() =>
  import("./components/debug/NotificationDebug").then((m) => ({
    default: m.NotificationDebug,
  })),
);
const WorkerRenderingTestHarness = React.lazy(() =>
  import("./components/timeline/workers/__tests__/WorkerRenderingTestHarness").then(
    (m) => ({ default: m.WorkerRenderingTestHarness }),
  ),
);
const DerivedAssetsShowcase = React.lazy(
  () => import("./pages/DerivedAssetsShowcase"),
);
const AuthDebugPage = React.lazy(() =>
  import("./pages/AuthDebugPage").then((m) => ({ default: m.AuthDebugPage })),
);
const Workflow = React.lazy(() => import("./pages/Workflow"));
const Timeline = React.lazy(() => import("./pages/Timeline"));
const Cast = React.lazy(() => import("./pages/Cast"));
const OrganisationMembers = React.lazy(
  () => import("./pages/OrganisationMembers"),
);
const OrganisationTeams = React.lazy(() => import("./pages/OrganisationTeams"));
const OrganisationSettings = React.lazy(
  () => import("./pages/OrganisationSettings"),
);
const OrganisationUsage = React.lazy(() => import("./pages/OrganisationUsage"));
const OrganisationBilling = React.lazy(
  () => import("./pages/OrganisationBilling"),
);
const OrganisationPolicies = React.lazy(
  () => import("./pages/OrganisationPolicies"),
);
const InspirePage = React.lazy(() =>
  import("./pages/InspirePage").then((m) => ({ default: m.InspirePage })),
);
const AcceptInvitationPage = React.lazy(() =>
  import("./pages/AcceptInvitationPage").then((m) => ({
    default: m.AcceptInvitationPage,
  })),
);
const VerifyEmailPage = React.lazy(() =>
  import("./pages/VerifyEmailPage").then((m) => ({
    default: m.VerifyEmailPage,
  })),
);
const AcceptTermsPage = React.lazy(() =>
  import("./pages/AcceptTermsPage").then((m) => ({
    default: m.AcceptTermsPage,
  })),
);
const TemplatesPage = React.lazy(() =>
  import("./pages/TemplatesPage").then((m) => ({ default: m.TemplatesPage })),
);
const BinPage = React.lazy(() =>
  import("./pages/Bin").then((m) => ({ default: m.BinPage })),
);
const SettingsPage = React.lazy(() =>
  import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const SupportPage = React.lazy(() =>
  import("./pages/SupportPage").then((m) => ({ default: m.SupportPage })),
);

/**
 * Param-preserving redirect for the legacy nested ticket path
 * (`/settings/support/tickets/:ticketId` → `/support/tickets/:ticketId`), so old
 * links and any deep-links that predate the top-level Support page keep working.
 */
const RedirectToSupportTicket: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  return <Navigate to={`/support/tickets/${ticketId ?? ""}`} replace />;
};
const CustomServicesPage = React.lazy(() =>
  import("./components/custom-services/CustomServicesPage").then((m) => ({
    default: m.CustomServicesPage,
  })),
);
const SubscriptionReturn = React.lazy(
  () => import("./components/settings/billing/SubscriptionReturn"),
);
const ChatPage = React.lazy(() =>
  import("./components/chat/ChatPage").then((m) => ({ default: m.ChatPage })),
);
import {
  VerticalNav,
  type VerticalNavRef,
} from "./components/navigation/VerticalNav";
import { ProjectSidePanel } from "./components/navigation/ProjectSidePanel";
import { ProjectTitleBar } from "./components/navigation/ProjectTitleBar";
import { ContentFilterBar } from "./components/navigation/ContentFilterBar";
import { useNavigation } from "./contexts/NavigationContext";

// Import CSS
import "./styles/theme.css";
import "./styles/animations.css";
import "./App.css";
import { Toaster } from "./components/shared/sonner";

// api client initialization
import "./services/apiService";
import { ConfirmationProvider } from "./hooks/useConfirmationDialog";
import { LoadingProvider } from "./components/shared/LoadingDialog";
import NotFound from "./pages/NotFound";
import { TooltipProvider } from "./components/ui/tooltip";
import { useUserOrganisation } from "./hooks/useUserOrganisation";
import { useSignedCookies } from "./hooks/useSignedCookies";
import { useAppStore } from "./stores/appStore";
import { InvitationBanner } from "./components/sharing/InvitationBanner";
import { EntitlementDebugPanel } from "./components/debug/EntitlementDebugPanel";

const ProjectSettings = React.lazy(() => import("./pages/ProjectSettings"));
const ShotsPage = React.lazy(() => import("./pages/ShotsPage"));
const CastsPage = React.lazy(() => import("./pages/CastsPage"));
const LibraryPage = React.lazy(() => import("./pages/LibraryPage"));

// Create query client instance
const queryClient = createQueryClient();

// Legacy redirect component for /projects/* -> /productions/*
const LegacyProjectsRedirect: React.FC = () => {
  const location = useLocation();
  const newPath = location.pathname.replace("/projects", "/productions");
  return <Navigate to={newPath + location.search} replace />;
};

// Legacy redirect component for /cameos/* -> /cast/*
const LegacyCameosRedirect: React.FC = () => {
  const location = useLocation();
  const newPath = location.pathname.replace("/cameos", "/cast");
  // Also migrate ?cameo= to ?cast= in query string
  const newSearch = location.search.replace(/([?&])cameo=/g, "$1cast=");
  return <Navigate to={newPath + newSearch} replace />;
};

// Grid background component that responds to theme
const GridBackground: React.FC<{ size: number; opacity: number }> = ({
  size,
  opacity,
}) => {
  const { theme } = useTheme();
  const gridImage = theme === "light" ? "/grid_purple.png" : "/grid_white.png";

  return (
    <div
      className="absolute inset-0 pointer-events-none z-0"
      style={{
        backgroundImage: `url(${gridImage})`,
        backgroundSize: `${size}px`,
        backgroundRepeat: "repeat",
        backgroundPosition: "center",
        opacity,
        transition: "background-size 1s ease-in-out, opacity 1s ease-in-out",
      }}
    />
  );
};

// Root component that provides all contexts
const Root: React.FC = () => {
  const location = useLocation();
  const [gridSize, setGridSize] = React.useState(100);
  const [gridOpacity, setGridOpacity] = React.useState(0.2);

  React.useEffect(() => {
    logger.info("Studio1 Frontend Alpha application started", {
      version: import.meta.env.VITE_APP_VERSION || "0.1.0",
      environment: import.meta.env.MODE || "development",
      correlationId: generateUUID(),
    });
  }, []);

  // Handle Vite's preload errors (power#1298) - emitted before React.lazy rejection
  React.useEffect(() => {
    const handlePreloadError = (event: Event) => {
      // Vite emits this as a CustomEvent with payload in detail
      const customEvent = event as CustomEvent<{ message?: string }>;
      const message = customEvent.detail?.message ?? "Preload failed";
      logger.warn("vite:preloadError", { message });

      if (isChunkLoadError(new Error(message))) {
        handleChunkLoadError(new Error(message));
      }
    };

    window.addEventListener("vite:preloadError", handlePreloadError);
    return () =>
      window.removeEventListener("vite:preloadError", handlePreloadError);
  }, []);

  // Animate grid size and opacity based on route
  // Grid is only visible on workflow canvas, hidden on project pages
  React.useEffect(() => {
    const isTimelinePage = location.pathname.includes("/video-editor");
    const isWorkflowCanvas = location.pathname.includes("/workflows/");
    const isProductionsPage = location.pathname.startsWith("/productions");

    if (isTimelinePage) {
      // Timeline editor - no grid (clean canvas)
      setGridSize(95);
      setGridOpacity(0);
    } else if (isWorkflowCanvas) {
      // Workflow canvas - visible grid
      setGridSize(95);
      setGridOpacity(0.2);
    } else if (isProductionsPage) {
      // Production pages (list, shots, cast, etc.) - no grid
      setGridSize(100);
      setGridOpacity(0);
    } else {
      // Other pages (inspire, templates, etc.) - no grid
      setGridSize(100);
      setGridOpacity(0);
    }
  }, [location.pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <AuthProvider>
            <ConfirmationProvider>
              <LoadingProvider>
                <div className="App h-screen flex flex-col bg-background text-foreground relative">
                  {/* Global grid pattern background */}
                  <GridBackground size={gridSize} opacity={gridOpacity} />

                  {/* Content above grid */}
                  <div className="relative z-10 h-full flex flex-col">
                    <Outlet />
                  </div>

                  {/* React Query Devtools - only in development */}
                  {import.meta.env.DEV && (
                    <ReactQueryDevtools initialIsOpen={false} />
                  )}
                  {/* Entitlement debug panel - only in development */}
                  {import.meta.env.DEV && <EntitlementDebugPanel />}
                </div>
                <Toaster />
                {/* Store-driven support dialog. Mounted once here, beside the
                    Toaster, because every store caller is global too: the
                    restricted-account gate's appeal CTA, an ErrorToast's
                    "Report", an entitlement DenialMessage, and useReportError.
                    Without this mount `useSupportDialogStore.open()` flips
                    state nothing renders, and all four silently no-op
                    (power#1067). Inert while closed — Radix unmounts the
                    content, so its ticket/contact-type queries never run. */}
                <ContactSupportDialogGlobal />
              </LoadingProvider>
            </ConfirmationProvider>
          </AuthProvider>
        </TooltipProvider>
        <MobileOverlay />
      </ThemeProvider>
    </QueryClientProvider>
  );
};

// Global WebSocket event consumers — registered once inside WebSocketProvider
const WebSocketEventConsumers: React.FC = () => {
  const { addMessageListener } = useWebSocketContext();
  useDerivedAssetEvents(addMessageListener);
  useTimelineExportEvents(addMessageListener);
  useActivityEvents(addMessageListener);
  useUnifiedEventConsumer(addMessageListener);
  useExecutionActivityListener(addMessageListener);
  useLoraEvents(addMessageListener);
  useChatRealtime(addMessageListener);
  return null;
};

// Inner layout that uses NavigationContext (must be inside NavigationProvider)
const ProtectedLayoutInner: React.FC = () => {
  const navRef = React.useRef<VerticalNavRef>(null);
  const { registerNavExitHandler } = useNavigation();
  const location = useLocation();

  // Poll for a newly deployed build (power#1224). Mounted here rather than in
  // `Root` because the watcher uses TanStack Query and `Root` is the component
  // that RENDERS QueryClientProvider — a useQuery there would have no client.
  // Being inside the protected shell also means we don't poll on the login page.
  useAppVersionWatcher();

  // Show project panel on /productions/* routes (including cast detail)
  // Hidden on /workflows/* (canvas) and /video-editor/* (timeline)
  const showProjectPanel = location.pathname.startsWith("/productions");

  // Register the nav slide-out handler
  React.useEffect(() => {
    registerNavExitHandler(() => {
      if (navRef.current) {
        return navRef.current.triggerSlideOut();
      }
      return Promise.resolve();
    });

    return () => {
      registerNavExitHandler(null);
    };
  }, [registerNavExitHandler]);

  return (
    <div className="flex flex-col h-full">
      <Header />
      <RestrictedAccountGate />
      <NewVersionBanner />
      <AccountRestrictionBanners />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <VerticalNav ref={navRef} />
        {showProjectPanel ? (
          // Project layout: Title bar spans full width, side panel below
          <div className="flex flex-col flex-1 min-h-0">
            <ProjectTitleBar />
            <div className="flex flex-1 min-h-0 overflow-hidden">
              <ProjectSidePanel />
              <main
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col"
                style={{ backgroundColor: "#111111" }}
              >
                {/* Gradient line under title bar - shifts based on active section */}
                <div
                  className="h-[2px] w-full flex-shrink-0"
                  style={{ background: getGradientForRoute(location.pathname) }}
                />
                <InvitationBanner />
                <ContentFilterBar />
                <div className="flex-1 min-h-0">
                  <ChunkErrorBoundary>
                    <RouteTransition />
                  </ChunkErrorBoundary>
                </div>
              </main>
            </div>
          </div>
        ) : (
          // Default layout: Full-width main content
          <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <InvitationBanner />
            <ChunkErrorBoundary>
              <RouteTransition />
            </ChunkErrorBoundary>
          </main>
        )}
      </div>
    </div>
  );
};

// Provider shell for all authenticated routes.
//
// This layout enforces auth and mounts the shared providers (WebSocket,
// Navigation, Collaboration) that the Header and the app depend on. It renders
// an <Outlet /> so that BOTH the /accept-terms page and the ToS-gated app
// shell live inside these providers — the Header on the ToS page needs
// NavigationProvider etc. to render.
//
// The ToS hard-gate (AcceptTermsGuard) is applied one level deeper, wrapping
// only the app shell, so /accept-terms stays reachable while
// `hasAcceptedTos === false` (no redirect loop).
const ProtectedProviders: React.FC = () => {
  // Fetch projects on all protected routes (for breadcrumbs and navigation).
  // Workflows query is disabled by default to avoid expensive per-item URL signing
  // on every route. Components that need workflows opt-in via enableWorkflowsQuery.
  // See #1303 for details.
  useProjects();

  // Fetch and populate the user's organisation membership into the Zustand store
  useUserOrganisation();

  // Fetch CloudFront signed cookies when a project is selected.
  // TanStack Query deduplication means child components calling
  // useSignedCookies(sameProjectId) share the cached result.
  const currentProjectId = useAppStore((s) => s.currentProject?.id);
  useSignedCookies(currentProjectId);

  return (
    <ProtectedRoute requireAuth={true}>
      <WebSocketProvider>
        <NavigationProvider>
          <CollaborationProvider>
            {/* WebSocketEventConsumers is mounted inside CollaborationProvider
                so the unified consumer can call useCollaboration() to apply
                workflow / timeline lock events to shared state. None of the
                other consumers in this component depend on being above the
                provider; the move is order-equivalent for them. */}
            <WebSocketEventConsumers />
            <Outlet />
          </CollaborationProvider>
        </NavigationProvider>
      </WebSocketProvider>
    </ProtectedRoute>
  );
};

// Create the router
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<Root />}>
      {/* Public routes */}
      <Route path="auth/callback" element={<OAuthHandler />} />
      <Route
        path="auth"
        element={
          <ProtectedRoute requireAuth={false}>
            <AuthPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="invitations/:token/accept"
        element={
          <ChunkErrorBoundary>
            <React.Suspense fallback={<div />}>
              <AcceptInvitationPage />
            </React.Suspense>
          </ChunkErrorBoundary>
        }
      />
      <Route
        path="email/verify/:token"
        element={
          <ChunkErrorBoundary>
            <React.Suspense fallback={<div />}>
              <VerifyEmailPage />
            </React.Suspense>
          </ChunkErrorBoundary>
        }
      />

      {/* Authenticated routes (providers + auth enforcement).
          /accept-terms lives here — auth-required but OUTSIDE the ToS gate. */}
      <Route element={<ProtectedProviders />}>
        {/* Accept Terms of Service — reachable while !hasAcceptedTos.
            Renders its own Header + Footer (no app nav shell). */}
        <Route
          path="accept-terms"
          element={
            <ChunkErrorBoundary>
              <React.Suspense fallback={<div />}>
                <AcceptTermsPage />
              </React.Suspense>
            </ChunkErrorBoundary>
          }
        />

        {/* ToS hard-gate: everything below is blocked until accepted */}
        <Route element={<AcceptTermsGuard />}>
          {/* App shell with header + vertical nav */}
          <Route element={<ProtectedLayoutInner />}>
            <Route index element={<Navigate to="/inspire" replace />} />
            <Route path="inspire" element={<InspirePage />} />
            {/* Legacy redirect for old custom-services route */}
            <Route
              path="custom-services"
              element={<Navigate to="/inspire" replace />}
            />
            <Route
              path="productions"
              element={<ProjectDashboard key="project-dashboard" />}
            />
            <Route
              path="productions/:projectId"
              element={<ProjectDashboard key="project-dashboard" />}
            />
            <Route
              path="productions/:projectId/scenes"
              element={<ShotsPage />}
            />
            <Route
              path="productions/:projectId/workflows"
              element={<ProjectDashboard key="project-dashboard" />}
            />
            <Route
              path="productions/:projectId/assets"
              element={<ProjectDashboard key="project-dashboard" />}
            />
            <Route path="productions/:projectId/cast" element={<CastsPage />} />
            <Route
              path="productions/:projectId/library"
              element={<LibraryPage />}
            />
            <Route
              path="productions/:projectId/settings"
              element={<ProjectSettings />}
            />
            <Route
              path="productions/:projectId/cast/:castId"
              element={<Cast />}
            />
            {/* Legacy route - redirect to new project-scoped URL */}
            <Route path="cast/:castId" element={<Cast />} />
            {/* Legacy /cameos routes - redirect to /cast */}
            <Route
              path="productions/:projectId/cameos"
              element={<LegacyCameosRedirect />}
            />
            <Route
              path="productions/:projectId/cameos/*"
              element={<LegacyCameosRedirect />}
            />
            <Route path="cameos/*" element={<LegacyCameosRedirect />} />
            {/* Legacy /projects routes - redirect to /productions */}
            <Route
              path="projects"
              element={<Navigate to="/productions" replace />}
            />
            <Route path="projects/*" element={<LegacyProjectsRedirect />} />
            <Route
              path="copilot"
              element={<Navigate to="/templates" replace />}
            />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="chat" element={<ChatPage />} />
            {/* Path-based deep link to a single channel (notifications + chat
                list open the exact conversation). Mirrors the support ticket
                route: a deeper path rendering the same page component. */}
            <Route path="chat/:channelId" element={<ChatPage />} />
            <Route path="bin" element={<BinPage />} />
            <Route
              path="settings"
              element={<Navigate to="/settings/email" replace />}
            />
            {/* Dedicated route: the single-segment settings/:section route below
                would not match the two-segment return path. */}
            <Route
              path="settings/billing/return"
              element={<SubscriptionReturn />}
            />
            {/* Support is now a top-level page (sibling of settings). */}
            <Route path="support" element={<SupportPage />} />
            <Route path="support/tickets/:ticketId" element={<SupportPage />} />
            {/* Backward-compat: the old nested support paths redirect to the
                top-level page (the deeper ticket path is declared before the
                generic settings/:section route so it takes precedence, and
                preserves :ticketId). */}
            <Route
              path="settings/support/tickets/:ticketId"
              element={<RedirectToSupportTicket />}
            />
            <Route
              path="settings/support"
              element={<Navigate to="/support" replace />}
            />
            <Route path="settings/:section" element={<SettingsPage />} />
            <Route path="workflows/:workflowId" element={<Workflow />} />
            <Route path="video-editor/:timelineId" element={<Timeline />} />
            {/* Legacy route redirect */}
            <Route
              path="dashboard"
              element={<Navigate to="/inspire" replace />}
            />
            <Route
              path="organisations/:organisationId"
              element={<OrgMembershipGuard />}
            >
              <Route element={<OrganisationLayout />}>
                <Route
                  path="members"
                  element={
                    <OrgErrorBoundary>
                      <OrganisationMembers />
                    </OrgErrorBoundary>
                  }
                />
                <Route
                  path="teams"
                  element={
                    <OrgErrorBoundary>
                      <OrganisationTeams />
                    </OrgErrorBoundary>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <OrgErrorBoundary>
                      <OrganisationSettings />
                    </OrgErrorBoundary>
                  }
                />
                <Route
                  path="usage"
                  element={
                    <OrgErrorBoundary>
                      <OrganisationUsage />
                    </OrgErrorBoundary>
                  }
                />
                <Route
                  path="billing"
                  element={
                    <OrgErrorBoundary>
                      <OrganisationBilling />
                    </OrgErrorBoundary>
                  }
                />
                <Route
                  path="policies"
                  element={
                    <OrgErrorBoundary>
                      <OrganisationPolicies />
                    </OrgErrorBoundary>
                  }
                />
                <Route
                  path="services"
                  element={
                    <OrgErrorBoundary>
                      <CustomServicesPage />
                    </OrgErrorBoundary>
                  }
                />
              </Route>
            </Route>

            {/* Debug routes - only in development */}
            {import.meta.env.DEV && (
              <>
                <Route path="auth/debug" element={<AuthDebugPage />} />
                <Route path="debug/websocket" element={<WebSocketDemo />} />
                <Route
                  path="debug/notifications"
                  element={<NotificationDebug />}
                />
                <Route
                  path="debug/worker-rendering"
                  element={<WorkerRenderingTestHarness />}
                />
                <Route
                  path="debug/derived-assets"
                  element={<DerivedAssetsShowcase />}
                />
              </>
            )}
          </Route>
        </Route>
      </Route>

      {/* Catch-all route for 404 errors */}
      <Route path="*" element={<NotFound />} />
    </Route>,
  ),
);

const App: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default App;
